import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { Trash2, Edit, Download, Eye, Grid, List } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import AdvancedImageViewer from '../components/AdvancedImageViewer'

const AllSurgicalSets = () => {
  const { API } = useContext(AuthContext)
  const navigate = useNavigate()
  const [surgicalSets, setSurgicalSets] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedSets, setSelectedSets] = useState([])
  const [exporting, setExporting] = useState(false)
  const [selectedSet, setSelectedSet] = useState(null)
  const [showImageDialog, setShowImageDialog] = useState(false)
  const [viewMode, setViewMode] = useState('cards')

  useEffect(() => {
    fetchSurgicalSets()
  }, [])

  const fetchSurgicalSets = async () => {
    try {
      const response = await axios.get(`${API}/surgical-sets?pushed=false`)
      setSurgicalSets(response.data)
    } catch (error) {
      console.error('Failed to fetch surgical sets:', error)
      toast.error('Failed to load surgical sets')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (setId) => {
    if (!window.confirm('Are you sure you want to delete this surgical set?')) return

    try {
      await axios.delete(`${API}/surgical-sets/${setId}`)
      toast.success('Surgical set deleted')
      fetchSurgicalSets()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete surgical set')
    }
  }

  const handleViewImage = (set) => {
    setSelectedSet(set)
    setShowImageDialog(true)
  }

  const handleEdit = (surgicalSet) => {
    navigate('/verify-surgical', {
      state: {
        surgical_items: surgicalSet.surgical_items,
        imageBase64: surgicalSet.image_base64,
        fileType: 'image',
        isEditing: true,
        setId: surgicalSet.id,
        setName: surgicalSet.set_name
      }
    })
  }

  const handleSelectSet = (setId, checked) => {
    if (checked) {
      setSelectedSets([...selectedSets, setId])
    } else {
      setSelectedSets(selectedSets.filter(id => id !== setId))
    }
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      const allSetIds = surgicalSets.map(set => set.id)
      setSelectedSets(allSetIds)
    } else {
      setSelectedSets([])
    }
  }

  const isAllSelected = surgicalSets.length > 0 && selectedSets.length === surgicalSets.length

  const handleBulkExport = async () => {
    if (selectedSets.length === 0) {
      toast.error('Please select at least one surgical set')
      return
    }

    if (!window.confirm(`Export ${selectedSets.length} surgical set(s) to Excel?`)) {
      return
    }

    setExporting(true)

    try {
      const response = await axios.post(
        `${API}/surgical-sets/bulk-export`,
        selectedSets,
        { responseType: 'blob' }
      )

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `surgical_items_${Date.now()}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Surgical sets exported successfully!')
      setSelectedSets([])
      fetchSurgicalSets()
    } catch (error) {
      console.error('Bulk export error:', error)
      toast.error('Failed to export surgical sets')
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <Layout title="All Surgical Data Sets">
        <div className="card">
          <p className="text-center text-gray-600">Loading...</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title="All Surgical Data Sets">
      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">All Surgical Data Sets</h2>
          <div className="flex gap-4 items-center">
            <div className="flex gap-1 bg-gray-100 rounded p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-4 py-2 rounded ${viewMode === 'cards' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid className="w-4 h-4 inline mr-2" />
                Cards
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4 inline mr-2" />
                List
              </button>
            </div>
            {selectedSets.length > 0 && (
              <Button onClick={handleBulkExport} disabled={exporting}>
                <Download className="w-4 h-4 mr-2" />
                Push to eHCMS ({selectedSets.length})
              </Button>
            )}
          </div>
        </div>

        {surgicalSets.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No surgical sets found</p>
            <Button onClick={() => navigate('/upload-surgical')}>
              Upload Surgical Items
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-md">
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
              />
              <label className="text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSelectAll(!isAllSelected)}>
                Select All ({surgicalSets.length} items)
              </label>
            </div>
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {surgicalSets.map(surgicalSet => (
                  <div key={surgicalSet.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3 mb-3">
                      <Checkbox
                        checked={selectedSets.includes(surgicalSet.id)}
                        onCheckedChange={(checked) => handleSelectSet(surgicalSet.id, checked)}
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-base text-gray-900">{surgicalSet.set_name}</h3>
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">
                        {surgicalSet.surgical_items.length} surgical item(s)
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Created: {new Date(surgicalSet.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewImage(surgicalSet)}
                        title="View Challan"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(surgicalSet)}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(surgicalSet.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {surgicalSets.map(surgicalSet => (
                  <div key={surgicalSet.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedSets.includes(surgicalSet.id)}
                        onCheckedChange={(checked) => handleSelectSet(surgicalSet.id, checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900">{surgicalSet.set_name}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {surgicalSet.surgical_items.length} surgical item(s)
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Created: {new Date(surgicalSet.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewImage(surgicalSet)}
                          title="View Challan"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(surgicalSet)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(surgicalSet.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showImageDialog && selectedSet && (
        <AdvancedImageViewer
          imageBase64={selectedSet.image_base64}
          fileType="image"
          onClose={() => {
            setShowImageDialog(false)
            setSelectedSet(null)
          }}
        />
      )}
    </Layout>
  )
}

export default AllSurgicalSets
